import React, { useRef } from "react";
import styled from "styled-components";
import { getImageFromFile } from "libs/files";
import { uploadFileToS3 } from "api/media-library/upload-file-to-s3";
import { SkedDataTable, SkedIcon, SkedButton, useSkedDataTable } from "ui";
import { PictureInput, TextInput } from "ui/ecosystems/forms";
import {
  useApprovalThemeMutations,
  useApprovalThemes,
} from "pages/approvals/approval-queries";

// Bootstrap FormGroup/ControlLabel replacements
const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const ControlLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 700;
`;

const ExternalCollabTheme = () => {
  const logoFile = useRef();
  const [themeState, themeDispatch] = useSkedDataTable();
  const { data: themes } = useApprovalThemes();
  const { createTheme, updateTheme, deleteTheme } = useApprovalThemeMutations();
  const updateImage = async (value, dispatch) => {
    dispatch({
      type: "updateField",
      payload: { field: "imageUrl", value },
    });
  };
  const logoUpdate = async (file, dispatch) => {
    logoFile.current = file;
    const value = await getImageFromFile(file);
    updateImage(value, dispatch);
  };

  const submit = async (state, dispatch) => {
    const logoS3Url = logoFile.current
      ? await uploadFileToS3({
          blob: logoFile.current,
          fileName: logoFile.current.name,
        })
      : state.editCopy.imageUrl;

    if (state.editCopy._id === "NEW") {
      await createTheme.mutateAsync({ ...state.editCopy, imageUrl: logoS3Url });
      // TODO black magic for dispatch?
      dispatch({
        type: "saveRow",
        payload: { row: state.editCopy },
      });
    } else {
      await updateTheme.mutateAsync({ ...state.editCopy, imageUrl: logoS3Url });
      // handle saving/loading state
      dispatch({
        type: "saveRow",
        payload: { row: state.editCopy },
      });
    }
  };

  const themeHeaderConfig = [
    {
      label: "Logo",
      tooltip: "Logo shown in top left of review portal.",
    },
    {
      label: "Name",
      tooltip: "An internal name for the theme.",
    },
    {
      label: "Company Name",
      tooltip: "Company name shown in top right of the review portal.",
    },
    {
      label: "Actions",
      className: "justify-end",
    },
  ];

  const themeBodyConfig = [
    {
      // eslint-disable-next-line react/prop-types
      viewRender({ imageUrl }) {
        return imageUrl ? (
          <img src={imageUrl} className="h-16 w-16" />
        ) : (
          <em className="text-gray-800">Not set</em>
        );
      },
      editRender(props, state, dispatch) {
        return (
          <FormGroup
            data-testid="themePictureInputWrapper"
            controlId="formGroupImageUrl"
          >
            <ControlLabel>Logo</ControlLabel>
            <PictureInput
              value={state.editCopy.imageUrl}
              onChange={(file) => logoUpdate(file, dispatch)}
              onRemove={() => updateImage("", dispatch)}
            />
          </FormGroup>
        );
      },
    },
    {
      // eslint-disable-next-line react/prop-types
      viewRender({ name }) {
        return name || <em className="text-gray-800">Not set</em>;
      },
      editRender(props, state, dispatch) {
        return (
          <FormGroup controlId="formGroupImageUrl">
            <ControlLabel>Theme name</ControlLabel>
            <TextInput
              data-testid="themeNameInput"
              value={state.editCopy.name}
              placeholder="Name"
              onChange={(value) =>
                dispatch({
                  type: "updateField",
                  payload: { field: "name", value: value },
                })
              }
            />
          </FormGroup>
        );
      },
    },
    {
      // eslint-disable-next-line react/prop-types
      viewRender({ companyName }) {
        return companyName || <em className="text-gray-800">Not set</em>;
      },
      editRender(props, state, dispatch) {
        return (
          <FormGroup controlId="formGroupImageUrl">
            <ControlLabel>Company name</ControlLabel>
            <TextInput
              data-testid="themeCompanyNameInput"
              type="text"
              value={state.editCopy.companyName}
              placeholder="ACME Co"
              onChange={(value) =>
                dispatch({
                  type: "updateField",
                  payload: { field: "companyName", value: value },
                })
              }
            />
          </FormGroup>
        );
      },
    },
    {
      viewRender(row, state, dispatch) {
        return (
          <div
            data-testid="themeActions"
            className="flex w-full justify-end items-center"
          >
            <SkedIcon
              className="mr-4"
              icon="icon-pencil"
              size="20"
              color="purple"
              tooltip="Edit theme"
              onClick={() => dispatch({ type: "editRow", payload: { row } })}
            />
            <SkedIcon
              className="mr-4"
              icon="trash"
              size="20"
              color="red"
              onClick={() => themeActionFunctions.onDelete(row._id)}
              tooltip="Delete theme"
            />
          </div>
        );
      },
      editRender(row, state, dispatch) {
        return (
          <div className="flex justify-end mt-64">
            <SkedButton
              data-testid="themeSaveButton"
              type="primary"
              size="small"
              className="mr-2"
              onClick={() => submit(state, dispatch)}
              disabled={!(state.editCopy.name && state.editCopy.companyName)}
            >
              Save
            </SkedButton>
            <SkedButton
              data-testid="themeCancelEditButton"
              type="outline"
              size="small"
              onClick={() => {
                dispatch({
                  type: "cancelEditing",
                });
              }}
            >
              Cancel
            </SkedButton>
          </div>
        );
      },
    },
  ];

  const themeActionFunctions = {
    onDelete: (themeId) => {
      if (confirm("Are you sure you want to delete this theme?")) {
        deleteTheme.mutate(themeId);
      }
    },
    // onCreate: () => {},
    // onEdit: () => {},
  };

  return (
    <div>
      <h3 className="mt-0">Themes</h3>
      <p>
        Set up the themes that your external reviewers will see when reviewing
        content.
      </p>
      {themes && (
        <SkedDataTable
          headerConfig={themeHeaderConfig}
          bodyConfig={themeBodyConfig}
          data={themes}
          state={themeState}
          dispatch={themeDispatch}
        />
      )}
      <SkedButton
        data-testid="addNewTheme"
        onClick={() => themeDispatch({ type: "addRow" })}
        type="outline"
        size="small"
        className=""
        disabled={themeState.editRow !== null}
      >
        New theme
      </SkedButton>
    </div>
  );
};

export default ExternalCollabTheme;
